import React from 'react';
import { observer } from 'mobx-react';
import MethodPopover from '../MethodPopover';
import interpolate from 'color-interpolate';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IEventType } from '../../stores/model/metamodel/event_type';

const methodHighlightColormap = interpolate(['#ffac2c', '#dc2376']);

/**
 * Props for {@link OETCell}.
 *
 * - object: the object type for the cell
 * - event: the event type for the cell
 * - selectable: is selection for the methods in the cell enabled?
 * - selected: the id of the currently selected element.
 * - highlightedMethods: the currently highlighted methods.
 * - onSelect: callback to call when a method is selected. Arguments: id.
 * - popovers: are method popovers enabled?
 * - isDragging: is the column to which this cell belongs being dragged right now?
 * - onClick: callback when the cell is clicked. Arguments: event id, object id.
 */
export interface OETCellProps {
  object: IObjectType;
  event: IEventType;
  selectable: boolean;
  selected: string | null;
  onSelect: (id: string) => void;
  popovers: boolean;
  isDragging: boolean;
  highlightedMethods: string[];
  onClick?: (eventId: string, objectId: string) => void;
}

/**
 * A cell in the OET.
 */
const OETCell = observer(function OETCell({
  object,
  event,
  selectable,
  selected,
  onSelect,
  popovers,
  isDragging,
  highlightedMethods,
  onClick,
}: OETCellProps) {
  const handleClick = () => {
    if (onClick) onClick(event.id, object.id);
  };

  const methods = object.methods.filter((m) => m.ownerEventType === event);
  const classes = ['oet-method-wrapper'];
  if (selectable) {
    classes.push('selectable');
  }
  const cellClasses: string[] = [];
  if (isDragging) {
    cellClasses.push('dragging');
  }
  return (
    <td onClick={handleClick} className={cellClasses.join(' ')}>
      {methods.map((m) => {
        const methodClasses = classes.slice();
        const methodStyles: React.CSSProperties = {};
        let methodExtras: React.ReactNode = null;
        if (selected === m.id) {
          methodClasses.push('selected');
        } else if (highlightedMethods.includes(m.id)) {
          const i = highlightedMethods.indexOf(m.id);
          methodStyles.color = methodHighlightColormap(i / highlightedMethods.length);
          methodExtras = <sup>{i + 1}</sup>;
        }
        // Was `td .secondary, td .hidden { opacity: 0.5 }` in the old SCSS;
        // converted to the opacity utility on the method wrapper.
        if (m.secondary) {
          methodClasses.push('opacity-50');
        }
        if (m.hidden) {
          methodClasses.push('opacity-50');
        }
        return (
          <div
            key={m.id}
            className={methodClasses.join(' ')}
            style={methodStyles}
            onClick={selectable ? (e) => onSelect(m.id) : undefined}
          >
            <MethodPopover
              key={m.id}
              preferPlace="below"
              method={m}
              showObject={true}
              enabled={popovers}
            >
              <div key={m.id}>
                {m.metadata}
                {m.isLooping() ? '*' : ''}
                {methodExtras}
              </div>
            </MethodPopover>
          </div>
        );
      })}
    </td>
  );
});

export default OETCell;
